import { birthDateSchema, freeTextSchema, personNameSchema, phoneNumberSchema, postalCodeSchema, TEXT_MAX_LENGTH } from '@1trading/fast-api-domain/forms';
import { z } from 'zod';

import i18n from '@/lib/i18n';
import { createPostalCodeRefinement, mainstreamCountrySchema } from '@/services/api/mainstream';
import { EMPTY_PHONE_NUMBER } from '@/utils/phone-number';

/** fast-api accepts a date of birth from this date up to today */
export const EARLIEST_BIRTH_DATE = '1900-01-01';

const identityAddressOfResidencySchema = z.object({
  addressLines: z.array(z.string().min(1)).min(1).max(2),
  country: mainstreamCountrySchema(),
  locality: z.string().min(1),
  postalCode: z.string(),
});

export type IdentityAddressOfResidency = z.infer<typeof identityAddressOfResidencySchema>;

export const TAX_RESIDENCY_SOURCE = {
  KYC: 'kyc',
  ONBOARDING: 'onboarding',
  PERIODIC_REVIEW: 'periodic-review',
} as const;

const identityTaxResidencySchema = z.object({
  affirmedAt: z.iso.datetime().optional(),
  residences: z
    .array(
      z.object({
        country: mainstreamCountrySchema(),
        taxId: z.string().min(1).optional(),
      }),
    )
    .min(1),
  source: z.enum(TAX_RESIDENCY_SOURCE).optional(),
});

const { refinement: postalCodeRefinement, params: postalCodeRefinementParams } = createPostalCodeRefinement<{
  addressOfResidency?: Pick<IdentityAddressOfResidency, 'country' | 'postalCode'>;
}>({
  getParts: (data) => ({ country: data?.addressOfResidency?.country, postCode: data?.addressOfResidency?.postalCode }),
  message: i18n.t('requiredField.error'),
  path: ['addressOfResidency', 'postalCode'],
});

export const identitySchema = z
  .object({
    addressOfResidency: identityAddressOfResidencySchema,
    birthInfo: z.object({
      country: mainstreamCountrySchema(),
      date: z.iso.date(),
      locality: z.string().min(1),
    }),
    citizenship: mainstreamCountrySchema(),
    citizenship2: mainstreamCountrySchema().optional(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    phoneNumber: z.object({
      areaCode: z.string().min(1),
      mobilePhone: z.string().min(1),
    }),
    /** @deprecated Superseded by taxResidency; read only to hint the previous number, as fast-api no longer takes it on submit */
    taxId: z.string().optional(),
    taxResidency: identityTaxResidencySchema,
  })
  .refine(postalCodeRefinement, postalCodeRefinementParams);

export type Identity = z.infer<typeof identitySchema>;

export const identityWithFallbacksSchema = z.object({
  addressOfResidency: z
    .object({
      addressLines: z
        .array(freeTextSchema({ errorPrefix: 'ADDRESS_LINE', maxLength: TEXT_MAX_LENGTH.SHORT }).catch(''))
        .min(1)
        .max(2)
        .catch([]),
      country: mainstreamCountrySchema().catch(''),
      locality: freeTextSchema({ errorPrefix: 'ADDRESS_LOCALITY', maxLength: TEXT_MAX_LENGTH.SHORT }).catch(''),
      postalCode: postalCodeSchema({ errorPrefix: 'ADDRESS_POSTAL_CODE' }).catch(''),
    })
    .catch({
      addressLines: [],
      country: '',
      locality: '',
      postalCode: '',
    }),
  birthInfo: z
    .object({
      country: mainstreamCountrySchema().catch(''),
      date: birthDateSchema({ errorPrefix: 'BIRTH_DATE' }).catch(''),
      locality: freeTextSchema({ errorPrefix: 'BIRTH_LOCALITY', maxLength: TEXT_MAX_LENGTH.SHORT }).catch(''),
    })
    .catch({
      country: '',
      date: '',
      locality: '',
    }),
  citizenship: mainstreamCountrySchema().catch(''),
  citizenship2: mainstreamCountrySchema().optional().catch(undefined),
  firstName: personNameSchema({ errorPrefix: 'FIRST_NAME' }).catch(''),
  lastName: personNameSchema({ errorPrefix: 'LAST_NAME' }).catch(''),
  // One catch for the pair, so an invalid half unlocks both: a number left locked would be resubmitted under whatever code is picked next
  phoneNumber: phoneNumberSchema().catch(EMPTY_PHONE_NUMBER),
  taxId: z.string().optional().catch(undefined),
  taxResidency: identityTaxResidencySchema.optional().catch(undefined),
});

export type IdentityWithFallbacks = z.infer<typeof identityWithFallbacksSchema>;
